import { createSelector } from 'reselect';
import flow from 'lodash/flow';
import filter from 'lodash/fp/filter';

import { GlobalState } from '../../../../types.global';
import { SearchTag } from '../types';

import { selectShortcuts, selectTopSites } from '../../../tiles/slice/selectors';

export const selectChoosenSearchTag = createSelector(
  (state: GlobalState) => state.search.$entities.searchTags,
  (state: GlobalState) => state.search.searchTags.activeId,
  (list, activeId): SearchTag =>
    activeId && list.find(({ u }) => u === activeId)
);

const BASE_WIDTH = 32 + 10;
const PER_LETTER = 6.9;
const TOTAL_WIDTH = 584;

const getSearchTagWidth = (searchTag: SearchTag) => BASE_WIDTH + searchTag.s.length * PER_LETTER;

const limitSearchSearchTags = (list: SearchTag[]) => {
  let widthLimit = TOTAL_WIDTH * 0.9;
  return list.reduce((acc, searchTag) => {
    widthLimit = widthLimit - getSearchTagWidth(searchTag);
    return widthLimit < 0 ? acc : [...acc, searchTag];
  }, []);
};

const DEFAULT_LIST = [
  'https://www.youtube.com/results?search_query={{{s}}}',
  'http://en.wikipedia.org/wiki/Special:Search?search={{{s}}}&go=Go',
  'http://www.imdb.com/find?q={{{s}}}&s=all',
  'https://www.reddit.com/search?q={{{s}}}',
  'https://mail.google.com/mail/u/0/#search/{{{s}}}',
];

const toHostnames = (list) => list
  .map((h) => {
    try {
      return `//${new URL(h.url).hostname}`;
    } catch (err) {
      return null;
    }
  })
  .filter(Boolean);

const selectDefaultSearchTags = createSelector(
  (state: GlobalState) => state.search.$entities.searchTags,
  (state: GlobalState) => state.search.$entities.history,
  selectShortcuts,
  selectTopSites,
  (searchTags, history, shortcuts, topSites) => {
    if (!searchTags.length) return [];

    const hostnames = [
      ...toHostnames(history || []),
      ...toHostnames([...shortcuts, ...topSites]),
        ...DEFAULT_LIST,
    ];

    const order = {};
    return [
      ...searchTags
        .filter((s) => {
          const index = hostnames.findIndex((h) => s.u.includes(h));
          order[s.u] = index;
          return index !== -1;
        })
        .sort((a, b) => order[a.u] - order[b.u]),
    ]
      .filter(({ u }, i, arr) => arr.findIndex((s) => s.u === u) === i);
  }
);

export const selectDisplayedSearchTags = createSelector(
  (state: GlobalState) => {
    const { term } = state.search.searchTags;
    return (term === null ? state.search.provided : term)?.toLowerCase();
  },
  selectChoosenSearchTag,
  (state: GlobalState) => state.search.$entities?.searchTags,
  selectDefaultSearchTags,
  (provided, choosen, list, defaultList): SearchTag[] => {

    if (!choosen && provided.length > 6) return [];

    const matchingList = (provided && provided.length >= 2) ? flow(
      filter(({ t }) =>
        t.find((keyword = '') => keyword.startsWith(provided) || provided.startsWith(keyword))
      ),
      limitSearchSearchTags,
    )(list) : [];

    if (matchingList.length) return matchingList;

    return limitSearchSearchTags(defaultList);
  }
);
